import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import { reportsAPI, userAPI } from '../utils/api';

const HeadquartersReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Date filters - default to last 30 days
  const getDefaultDateFrom = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const getDefaultDateTo = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState(getDefaultDateTo());

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadUsers();
      loadReport();
    }
  }, []);

  // Reload report when user selection or dates change
  useEffect(() => {
    if (hasLoadedRef.current) {
      loadReport();
    }
  }, [selectedUserId, dateFrom, dateTo]);

  const loadUsers = async () => {
    try {
      const res = await userAPI.getAll();
      // Filter only staff users (branch accounts)
      const staffUsers = res.data.data.filter(user => user.userType === 'staff');
      setUsers(staffUsers);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (selectedUserId) params.userId = selectedUserId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await reportsAPI.getHeadquarters(params);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดรายงาน');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('th-TH').format(num || 0);
  };

  if (loading) {
    return (
      <Container className="main-content text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">กำลังโหลดรายงาน...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="main-content">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-graph-up-arrow me-2"></i>
            รายงานสำหรับสำนักงานใหญ่
          </h2>
          <p className="text-muted">ภาพรวมข้อมูลทั้งหมดในระบบ (ค่าเริ่มต้น: 30 วันล่าสุด)</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>วันที่เริ่มต้น</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>วันที่สิ้นสุด</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>กรองตามสาขา</Form.Label>
            <Form.Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">ทุกสาขา</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.fullName || user.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Financial Overview */}
      <h4 className="mb-3">
        <i className="bi bi-currency-dollar me-2"></i>
        ภาพรวมการเงิน
      </h4>
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stats-card border-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มูลค่าคำสั่งซื้อที่อนุมัติ (ทั้งหมด)</p>
                  <h3 className="mb-0 text-primary">{formatCurrency(data.financial.totalApprovedValue)}</h3>
                </div>
                <div className="text-primary">
                  <i className="bi bi-cash-stack" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มูลค่าเดือนนี้</p>
                  <h4 className="mb-0 text-success">{formatCurrency(data.financial.thisMonthApprovedValue)}</h4>
                </div>
                <div className="text-success">
                  <i className="bi bi-calendar-month" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มูลค่าสัปดาห์นี้</p>
                  <h4 className="mb-0 text-info">{formatCurrency(data.financial.thisWeekApprovedValue)}</h4>
                </div>
                <div className="text-info">
                  <i className="bi bi-calendar-week" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มูลค่าเฉลี่ยต่อคำสั่งซื้อ</p>
                  <h4 className="mb-0">{formatCurrency(data.financial.avgOrderValue)}</h4>
                </div>
                <div className="text-secondary">
                  <i className="bi bi-calculator" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มูลค่ารอดำเนินการ</p>
                  <h4 className="mb-0 text-warning">{formatCurrency(data.financial.pendingOrderValue)}</h4>
                </div>
                <div className="text-warning">
                  <i className="bi bi-hourglass-split" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">จำนวนวัตถุดิบทั้งหมด</p>
                  <h4 className="mb-0">{formatNumber(data.inventory.totalMaterials)} รายการ</h4>
                </div>
                <div className="text-info">
                  <i className="bi bi-box-seam" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Statistics */}
      <h4 className="mb-3 mt-4">
        <i className="bi bi-clipboard-data me-2"></i>
        สถิติคำสั่งซื้อ
      </h4>
      <Row className="mb-4">
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">คำสั่งซื้อทั้งหมด</p>
              <h2 className="mb-0">{formatNumber(data.orders.total)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">วันนี้</p>
              <h2 className="mb-0 text-info">{formatNumber(data.orders.today)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">สัปดาห์นี้</p>
              <h2 className="mb-0">{formatNumber(data.orders.thisWeek)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">รอดำเนินการ</p>
              <h2 className="mb-0 text-warning">{formatNumber(data.orders.pending)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">อนุมัติแล้ว</p>
              <h2 className="mb-0 text-success">{formatNumber(data.orders.approved)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <p className="text-muted mb-1">อัตราการอนุมัติ</p>
              <h2 className="mb-0 text-primary">{data.orders.approvalRate}%</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delivery Statistics */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">มีกำหนดวันจัดส่งแล้ว</p>
                  <h3 className="mb-0 text-success">{formatNumber(data.delivery.withDeliveryDate)}</h3>
                </div>
                <div className="text-success">
                  <i className="bi bi-truck" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">รอกำหนดวันจัดส่ง</p>
                  <h3 className="mb-0 text-warning">{formatNumber(data.delivery.withoutDeliveryDate)}</h3>
                </div>
                <div className="text-warning">
                  <i className="bi bi-clock-history" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Materials by Quantity */}
      <h4 className="mb-3 mt-4">
        <i className="bi bi-boxes me-2"></i>
        วัตถุดิบที่สั่งบ่อย (จำนวนสูงสุด 10 อันดับ)
      </h4>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>รหัสวัตถุดิบ</th>
                    <th>ชื่อวัตถุดิบ</th>
                    <th>หมวดหมู่</th>
                    <th className="text-end">จำนวนที่สั่ง</th>
                    <th className="text-end">มูลค่ารวม</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topMaterialsByQuantity && data.topMaterialsByQuantity.length > 0 ? (
                    data.topMaterialsByQuantity.map((item, index) => (
                      <tr key={item.materialId}>
                        <td>
                          <Badge bg={index < 3 ? 'success' : 'secondary'}>
                            {index + 1}
                          </Badge>
                        </td>
                        <td><code>{item.code || '-'}</code></td>
                        <td><strong>{item.name || '-'}</strong></td>
                        <td>
                          <Badge bg="info">{item.category || 'ไม่ระบุ'}</Badge>
                        </td>
                        <td className="text-end">
                          <strong className="text-success">
                            {formatNumber(item.totalQuantity)} {item.unit}
                          </strong>
                        </td>
                        <td className="text-end">
                          {formatCurrency(item.totalValue)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        ไม่มีข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cost by Category */}
      <h4 className="mb-3 mt-4">
        <i className="bi bi-pie-chart me-2"></i>
        ต้นทุนตามหมวดหมู่
      </h4>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>หมวดหมู่</th>
                    <th className="text-end">จำนวนรายการ</th>
                    <th className="text-end">ต้นทุนรวม</th>
                    <th className="text-end">สัดส่วน</th>
                  </tr>
                </thead>
                <tbody>
                  {data.costByCategory && data.costByCategory.length > 0 ? (
                    (() => {
                      const totalCost = data.costByCategory.reduce(
                        (sum, item) => sum + parseFloat(item.totalCost || 0),
                        0
                      );
                      return data.costByCategory.map((item) => {
                        const percentage = totalCost > 0
                          ? ((parseFloat(item.totalCost) / totalCost) * 100).toFixed(2)
                          : 0;
                        return (
                          <tr key={item.category || 'unknown'}>
                            <td>
                              <Badge bg="primary">
                                {item.category || 'ไม่ระบุ'}
                              </Badge>
                            </td>
                            <td className="text-end">{formatNumber(item.orderCount)}</td>
                            <td className="text-end">
                              <strong>{formatCurrency(item.totalCost)}</strong>
                            </td>
                            <td className="text-end">
                              <Badge bg="info">{percentage}%</Badge>
                            </td>
                          </tr>
                        );
                      });
                    })()
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        ไม่มีข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Orders by Branch */}
      <h4 className="mb-3 mt-4">
        <i className="bi bi-building me-2"></i>
        คำสั่งซื้อตามสาขา / ผู้ใช้งาน (10 อันดับสูงสุด)
      </h4>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>ผู้ใช้งาน</th>
                    <th>ประเภท</th>
                    <th className="text-end">จำนวนคำสั่งซื้อ</th>
                    <th className="text-end">มูลค่ารวม</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ordersByBranch && data.ordersByBranch.length > 0 ? (
                    data.ordersByBranch.map((item, index) => (
                      <tr key={item.username || index}>
                        <td>{index + 1}</td>
                        <td>
                          <i className="bi bi-person me-1"></i>
                          <strong>{item.username || '-'}</strong>
                        </td>
                        <td>
                          <Badge bg="secondary">{item.userType || 'N/A'}</Badge>
                        </td>
                        <td className="text-end">{formatNumber(item.orderCount)}</td>
                        <td className="text-end">
                          <strong>{formatCurrency(item.totalValue)}</strong>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        ไม่มีข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Orders Trend (Last 30 Days) */}
      <h4 className="mb-3 mt-4">
        <i className="bi bi-graph-up me-2"></i>
        แนวโน้มคำสั่งซื้อ (30 วันล่าสุด)
      </h4>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>วันที่</th>
                    <th className="text-end">จำนวนคำสั่งซื้อ</th>
                    <th className="text-end">มูลค่ารวม</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ordersTrend && data.ordersTrend.length > 0 ? (
                    data.ordersTrend.map((item) => (
                      <tr key={item.date}>
                        <td>
                          <i className="bi bi-calendar3 me-2"></i>
                          {new Date(item.date).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="text-end">{formatNumber(item.count)}</td>
                        <td className="text-end">
                          <strong>{formatCurrency(item.totalValue)}</strong>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-4">
                        ไม่มีข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default HeadquartersReport;
